import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Shield,
  RefreshCw,
  Download,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScanResults({ user, logout, token }) {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedVuln, setExpandedVuln] = useState(null);

  useEffect(() => {
    fetchScan();
    const interval = setInterval(() => {
      if (scan?.status === 'running' || scan?.status === 'pending') {
        fetchScan();
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [scanId, scan?.status]);

  const fetchScan = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${BACKEND_URL}/api/scans/${scanId}`, { 
        headers, 
        withCredentials: true 
      });
      setScan(response.data);
    } catch (error) {
      console.error('Error fetching scan:', error);
      toast.error("Failed to load scan results");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: "text-red-500 bg-red-500/10 border-red-500/20",
      high: "text-orange-500 bg-orange-500/10 border-orange-500/20",
      medium: "text-yellow-500 bg-yellow-500/10 border-yellow-500/20",
      low: "text-blue-500 bg-blue-500/10 border-blue-500/20",
      info: "text-slate-400 bg-slate-500/10 border-slate-500/20"
    };
    return colors[severity] || colors.info;
  };

  const getStatusColor = (status) => {
    const colors = {
      pass: "text-emerald-500 bg-emerald-500/10",
      fail: "text-red-500 bg-red-500/10",
      partial: "text-yellow-500 bg-yellow-500/10",
      not_applicable: "text-slate-400 bg-slate-500/10"
    };
    return colors[status] || colors.not_applicable;
  };

  const handleGenerateReport = async (reportType) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${BACKEND_URL}/api/reports`, {
        scan_id: scanId,
        report_type: reportType
      }, { headers, withCredentials: true });
      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report generated!`);
      navigate('/reports');
    } catch (error) {
      toast.error("Failed to generate report");
    }
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading scan results...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!scan) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="text-center py-12">
          <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Scan not found</h3>
          <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
        </div>
      </DashboardLayout>
    );
  }

  const isRunning = scan.status === 'running' || scan.status === 'pending';

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="scan-results-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold font-[Chivo]">{scan.name}</h2>
              <Badge className={
                scan.status === 'completed' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                scan.status === 'running' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' :
                scan.status === 'failed' ? 'bg-red-500/10 text-red-500 border-red-500/20' :
                'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
              }>
                {scan.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {scan.description || `${scan.config.scan_type} scan • Started ${new Date(scan.created_at).toLocaleString()}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isRunning && (
              <Button variant="outline" onClick={fetchScan} data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            )}
            {scan.status === 'completed' && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleGenerateReport('executive')} data-testid="generate-executive-btn">
                  <FileText className="h-4 w-4 mr-2" />
                  Executive Report
                </Button>
                <Button variant="outline" onClick={() => handleGenerateReport('technical')} data-testid="generate-technical-btn">
                  <FileText className="h-4 w-4 mr-2" />
                  Technical Report
                </Button>
                {scan.compliance_results?.length > 0 && (
                  <Button variant="outline" onClick={() => handleGenerateReport('compliance')} data-testid="generate-compliance-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Compliance Report
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Progress (if running) */}
        {isRunning && (
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Clock className="h-6 w-6 text-primary animate-pulse" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Scan in progress...</span>
                    <span className="text-sm text-muted-foreground">{scan.progress}%</span>
                  </div>
                  <Progress value={scan.progress} className="h-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary (if completed) */}
        {scan.status === 'completed' && scan.summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-card border-border" data-testid="summary-total">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold font-[Chivo]">{scan.summary.total_vulnerabilities}</p>
                <p className="text-sm text-muted-foreground">Total Findings</p>
              </CardContent>
            </Card>
            {['critical', 'high', 'medium', 'low'].map((severity) => (
              <Card key={severity} className="bg-card border-border" data-testid={`summary-${severity}`}>
                <CardContent className="pt-6 text-center">
                  <p className={`text-3xl font-bold font-[Chivo] ${
                    severity === 'critical' ? 'text-red-500' :
                    severity === 'high' ? 'text-orange-500' :
                    severity === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                  }`}>
                    {scan.summary.severity_breakdown?.[severity] || 0}
                  </p>
                  <p className="text-sm text-muted-foreground capitalize">{severity}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Risk Score */}
        {scan.status === 'completed' && scan.summary && (
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Overall Risk Score</p>
                  <p className="text-4xl font-bold font-[Chivo]">
                    <span className={
                      scan.summary.risk_score >= 70 ? 'text-red-500' :
                      scan.summary.risk_score >= 40 ? 'text-yellow-500' : 'text-emerald-500'
                    }>
                      {scan.summary.risk_score}
                    </span>
                    <span className="text-muted-foreground text-lg">/100</span>
                  </p>
                </div>
                <div className={`h-16 w-16 rounded-full flex items-center justify-center ${
                  scan.summary.risk_score >= 70 ? 'bg-red-500/10' :
                  scan.summary.risk_score >= 40 ? 'bg-yellow-500/10' : 'bg-emerald-500/10'
                }`}>
                  {scan.summary.risk_score >= 70 ? (
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  ) : scan.summary.risk_score >= 40 ? (
                    <AlertTriangle className="h-8 w-8 text-yellow-500" />
                  ) : (
                    <CheckCircle className="h-8 w-8 text-emerald-500" />
                  )}
                </div>
              </div>
              <div className="mt-4">
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      scan.summary.risk_score >= 70 ? 'bg-red-500' :
                      scan.summary.risk_score >= 40 ? 'bg-yellow-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${scan.summary.risk_score}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for Vulnerabilities and Compliance */}
        {scan.status === 'completed' && (
          <Tabs defaultValue="vulnerabilities" className="space-y-4">
            <TabsList className="bg-card border border-border">
              <TabsTrigger value="vulnerabilities" data-testid="tab-vulnerabilities">
                Vulnerabilities ({scan.vulnerabilities?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="compliance" data-testid="tab-compliance">
                Compliance ({scan.compliance_results?.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="vulnerabilities">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-[Chivo]">Vulnerability Findings</CardTitle>
                  <CardDescription>
                    Security vulnerabilities discovered during the scan
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {scan.vulnerabilities?.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                      <p className="text-lg font-medium">No vulnerabilities found</p>
                      <p className="text-muted-foreground">Your system passed all vulnerability checks</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[600px] pr-4">
                      <div className="space-y-3">
                        {scan.vulnerabilities.map((vuln, index) => (
                          <motion.div
                            key={vuln.vuln_id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className={`border rounded-sm overflow-hidden ${getSeverityColor(vuln.severity)} border`}
                          >
                            <div 
                              className="p-4 cursor-pointer"
                              onClick={() => setExpandedVuln(expandedVuln === vuln.vuln_id ? null : vuln.vuln_id)}
                              data-testid={`vuln-${vuln.vuln_id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-sm ${getSeverityColor(vuln.severity)}`}>
                                    <AlertTriangle className="h-4 w-4" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{vuln.title}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge variant="outline" className={getSeverityColor(vuln.severity)}>
                                        {vuln.severity.toUpperCase()}
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">{vuln.category}</span>
                                      {vuln.cvss_score && (
                                        <span className="text-xs text-muted-foreground">
                                          CVSS: {vuln.cvss_score.toFixed(1)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {expandedVuln === vuln.vuln_id ? (
                                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                )}
                              </div>
                            </div>
                            
                            {expandedVuln === vuln.vuln_id && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="px-4 pb-4 space-y-4 border-t border-border/50 pt-4"
                              >
                                <div>
                                  <p className="text-sm font-medium mb-1">Description</p>
                                  <p className="text-sm text-muted-foreground">{vuln.description}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium mb-1">Affected Component</p>
                                  <code className="text-sm bg-background px-2 py-1 rounded font-mono">
                                    {vuln.affected_component}
                                  </code>
                                </div>
                                <div>
                                  <p className="text-sm font-medium mb-1">Remediation</p>
                                  <p className="text-sm text-muted-foreground">{vuln.remediation}</p>
                                </div>
                                {vuln.ai_analysis && (
                                  <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Sparkles className="h-4 w-4 text-primary" />
                                      <p className="text-sm font-medium text-primary">AI Analysis</p>
                                    </div>
                                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                      {vuln.ai_analysis}
                                    </p>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="compliance">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-[Chivo]">Compliance Results</CardTitle>
                  <CardDescription>
                    Compliance assessment results by framework
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {scan.compliance_results?.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-lg font-medium">No compliance checks performed</p>
                      <p className="text-muted-foreground">This scan did not include compliance assessments</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[600px] pr-4">
                      {/* Group by framework */}
                      {Object.entries(
                        scan.compliance_results.reduce((acc, check) => {
                          if (!acc[check.framework]) acc[check.framework] = [];
                          acc[check.framework].push(check);
                          return acc;
                        }, {})
                      ).map(([framework, checks]) => {
                        const passed = checks.filter(c => c.status === 'pass').length;
                        const total = checks.length;
                        const percentage = Math.round((passed / total) * 100);
                        
                        return (
                          <div key={framework} className="mb-6">
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-lg font-bold font-[Chivo]">{framework}</h3>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  {passed}/{total} controls passed
                                </span>
                                <Badge className={
                                  percentage >= 80 ? 'bg-emerald-500/10 text-emerald-500' :
                                  percentage >= 50 ? 'bg-yellow-500/10 text-yellow-500' :
                                  'bg-red-500/10 text-red-500'
                                }>
                                  {percentage}%
                                </Badge>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {checks.map((check) => (
                                <div 
                                  key={check.check_id}
                                  className="flex items-center justify-between p-3 border border-border rounded-sm"
                                  data-testid={`compliance-${check.check_id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`p-1.5 rounded-sm ${getStatusColor(check.status)}`}>
                                      {check.status === 'pass' ? (
                                        <CheckCircle className="h-4 w-4" />
                                      ) : check.status === 'fail' ? (
                                        <AlertCircle className="h-4 w-4" />
                                      ) : (
                                        <AlertTriangle className="h-4 w-4" />
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium">
                                        {check.control_id}: {check.control_name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">{check.evidence}</p>
                                    </div>
                                  </div>
                                  <Badge className={getStatusColor(check.status)}>
                                    {check.status.replace('_', ' ').toUpperCase()}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
